import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { AdminOperationDefaultService } from './services/admin-operation-default.service';
import {
  CreateOperationProfileRequest,
  Operation,
  OperationProfile,
  OperationProfileRequest,
  OperationRequest,
} from '../operation-profile/models/operation-profile.model';
import { ProfileDialogComponent, DialogMode } from '../operation-profile/dialogs/profile-dialog.component';
import { OperationDialogComponent } from '../operation-profile/dialogs/operation-dialog.component';
import { ToastService } from '../../shared/services/toast-service';
import { ModalService } from '../../shared/components/modal/modal.service';
import { LanguageService } from '../../core/i18n/language.service';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import { GridColumn, GridSort, gridColumns } from '../../shared/components/grid/models/grid.model';
import { Icon } from '../../shared/components/icon/icon';
import { ScheduleStrip, ScheduleBand } from '../../shared/components/schedule-strip/schedule-strip';
import { IlluminationBar } from '../../shared/components/illumination-bar/illumination-bar';
import { PowerThresholdBar } from '../../shared/components/power-threshold-bar/power-threshold-bar';
import { LabelBadge, LabelBadgeDot } from '../../shared/components/label-badge/label-badge';
import { Badge } from '../../shared/components/badge/badge';
import { Button } from '../../shared/components/button/button';

/** Detail-panel tab within the default inspector. */
type DetailTab = 'overview' | 'operations';

/**
 * Admin screen for platform-default operation profiles. Mirrors the tenant
 * OperationsComponent master/detail, minus the inheritance/override/fork UI
 * (defaults have none). The admin API has no per-operation endpoints, so every
 * operation add/edit/delete re-PUTs the whole default via updateDefault().
 */
@Component({
  selector: 'admin-operation-defaults',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    Icon,
    ScheduleStrip,
    IlluminationBar,
    PowerThresholdBar,
    Grid,
    GridPage,
    GridCellDirective,
    GridDetailDirective,
    ProfileDialogComponent,
    OperationDialogComponent,
    LabelBadge,
    Badge,
    Button,
  ],
  templateUrl: './admin-operation-defaults.component.html',
})
export class AdminOperationDefaultsComponent implements OnInit {
  private readonly service = inject(AdminOperationDefaultService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  /** Full default list (the endpoint is not paginated). */
  readonly defaults = signal<OperationProfile[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly sort = signal<GridSort>({ sortBy: 'created', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  /** Master/detail link: the default shown in the inspector. */
  readonly selectedDefault = signal<OperationProfile | null>(null);
  readonly detailTab = signal<DetailTab>('overview');

  // Dialogs + save state
  readonly isProfileDialogOpen = signal(false);
  readonly isProfileSaving = signal(false);
  readonly isOperationDialogOpen = signal(false);
  readonly dialogMode = signal<DialogMode>('add');
  readonly editingDefault = signal<OperationProfile | null>(null);
  readonly editingOperation = signal<Operation | null>(null);

  readonly defaultId = (p: OperationProfile) => String(p.id);
  readonly asDefault = (row: unknown): OperationProfile => row as OperationProfile;

  /** Operations of the selected default (rendered in the Operations tab). */
  readonly operations = computed<Operation[]>(() => this.selectedDefault()?.operations ?? []);

  /** Client-side sort + page over the full list (the API returns everything). */
  readonly sortedDefaults = computed<OperationProfile[]>(() => {
    const { sortBy, sortDirection } = this.sort();
    const dir = sortDirection === 'ASC' ? 1 : -1;
    const rows = [...this.defaults()];
    rows.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortBy];
      const bv = (b as unknown as Record<string, unknown>)[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return rows;
  });

  readonly pagedDefaults = computed<OperationProfile[]>(() => {
    const start = this.page() * this.size();
    return this.sortedDefaults().slice(start, start + this.size());
  });

  readonly totalElements = computed(() => this.defaults().length);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalElements() / this.size())));

  readonly columns = computed<GridColumn<OperationProfile>[]>(() => {
    this.languageService.current();
    const t = (k: string) => this.transloco.translate(`adminOperationDefaults.columns.${k}`);
    return gridColumns<OperationProfile>([
      { key: 'operationProfileId', label: t('profile'), type: 'template', template: 'profile', sortable: true, alwaysVisible: true, width: 210 },
      { key: 'status', label: t('status'), type: 'template', template: 'status', sortable: false, width: 110 },
      { key: 'powerMode', label: t('powerMode'), type: 'template', template: 'powerMode', sortable: true, width: 110 },
      { key: 'schedule', label: t('schedule'), type: 'template', template: 'schedule', sortable: false, width: 190 },
      { key: 'render', label: t('render'), type: 'text', sortable: false, width: 90, value: (p) => this.rowRender(p) },
      { key: 'connection', label: t('connection'), type: 'text', sortable: false, width: 100, value: (p) => this.rowConnection(p) },
      { key: 'week', label: t('week'), type: 'template', template: 'week', sortable: false, width: 170 },
    ]);
  });

  ngOnInit(): void {
    this.loadDefaults();
  }

  private loadDefaults(): void {
    this.isLoading.set(true);
    this.service
      .getDefaults()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.defaults.set(response.data);
          this.ensureSelection(response.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.t('toasts.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
  }

  onPageChange(page: number): void {
    this.page.set(page);
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
  }

  onRowClick(def: OperationProfile): void {
    this.selectedDefault.set(def);
    this.detailTab.set('overview');
  }

  /** Keep the detail pane populated: fall back to the first row when the current
   *  selection is gone (deleted) or absent. */
  private ensureSelection(defaults: OperationProfile[]): void {
    const current = this.selectedDefault();
    const stillPresent = current != null && defaults.some((d) => d.id === current.id);
    if (stillPresent) {
      this.selectedDefault.set(defaults.find((d) => d.id === current!.id) ?? null);
      return;
    }
    this.selectedDefault.set(defaults[0] ?? null);
  }

  setTab(tab: DetailTab): void {
    this.detailTab.set(tab);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`adminOperationDefaults.${key}`, params);
  }

  // ── Display helpers (shared with the tenant screen's formatting) ──────────

  private static readonly OP_COLORS = [
    'var(--color-brand)',
    'var(--color-warning)',
    'var(--color-success)',
    'var(--color-caution)',
  ];

  daysLabel(days: string[]): string {
    const dt = (k: string) => this.transloco.translate(`operations.days.${k}`);
    if (!days?.length) return this.transloco.translate('operations.na');
    const norm = days.map((d) => d.slice(0, 3).toUpperCase());
    if (norm.length === 7) return dt('everyDay');
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    const weekend = ['SAT', 'SUN'];
    if (norm.length === 5 && weekdays.every((d) => norm.includes(d))) return dt('weekdays');
    if (norm.length === 2 && weekend.every((d) => norm.includes(d))) return dt('weekends');
    return norm.map((d) => d[0] + d.slice(1).toLowerCase()).join(', ');
  }

  windowLabel(op: { startTime: string | null; endTime: string | null }): string {
    const { startTime, endTime } = op;
    if (!startTime || !endTime) return this.transloco.translate('operations.na');
    if (startTime === '00:00' && (endTime === '23:59' || endTime === '24:00')) {
      return this.transloco.translate('operations.window.allDay');
    }
    return `${startTime}-${endTime}`;
  }

  dateRangeLabel(op: { startDate: string | null; endDate: string | null }): string {
    if (!op.startDate) return this.transloco.translate('operations.na');
    const noEnd = this.transloco.translate('operations.settings.noEnd');
    const end = !op.endDate || op.endDate.startsWith('9999') ? noEnd : op.endDate;
    return `${op.startDate} -> ${end}`;
  }

  operationScheduleLabel(op: Operation): string {
    return `${this.daysLabel(op.days)} · ${this.windowLabel(op)} · ${this.dateRangeLabel(op)}`;
  }

  private effectiveOperation(def: OperationProfile): Operation | null {
    return def.operations?.[0] ?? null;
  }

  rowScheduleLabel(def: OperationProfile): string {
    const op = this.effectiveOperation(def);
    if (!op) return this.t('row.defaults');
    return `${this.daysLabel(op.days)} · ${this.windowLabel(op)}`;
  }

  rowRender(def: OperationProfile): string {
    return this.formatInterval(this.effectiveOperation(def)?.renderInterval ?? def.renderInterval);
  }
  rowConnection(def: OperationProfile): string {
    return this.formatInterval(this.effectiveOperation(def)?.connectionInterval ?? def.connectionInterval);
  }

  rowFlag(def: OperationProfile): 'active' | 'sleep' | 'shutdown' | null {
    const op = this.effectiveOperation(def);
    if (!op) return null;
    if (op.shutdown) return 'shutdown';
    if (op.sleep) return 'sleep';
    return 'active';
  }

  flagDot(flag: 'active' | 'sleep' | 'shutdown' | null): 'green' | 'blue' | 'red' | 'gray' {
    switch (flag) {
      case 'active':
        return 'green';
      case 'sleep':
        return 'blue';
      case 'shutdown':
        return 'red';
      default:
        return 'gray';
    }
  }

  powerModeDot(mode: string): 'blue' | 'yellow' {
    return this.powerModeColor(mode) === 'blue' ? 'blue' : 'yellow';
  }

  batteryStatusDot(status: string): LabelBadgeDot | null {
    switch (this.batteryStatusColor(status)) {
      case 'green':
        return 'green';
      case 'orange':
        return 'orange';
      case 'red':
        return 'red';
      default:
        return null;
    }
  }

  formatInterval(ms: number | null | undefined): string {
    if (ms == null) return '-';
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds} s`;
    const m = seconds / 60;
    return `${Number.isInteger(m) ? m : m.toFixed(1)} min`;
  }

  operationsCount(def: OperationProfile): number {
    return def.operations?.length ?? 0;
  }

  illuminationOf(op: Operation): number {
    return op.illumination ?? 0;
  }

  operationColor(index: number): string {
    return AdminOperationDefaultsComponent.OP_COLORS[index % AdminOperationDefaultsComponent.OP_COLORS.length];
  }

  coverageBands(): ScheduleBand[] {
    return this.bandsFrom(this.operations());
  }

  defaultBands(def: OperationProfile): ScheduleBand[] {
    return this.bandsFrom(def.operations ?? []);
  }

  private bandsFrom(ops: Operation[]): ScheduleBand[] {
    return ops.map((op, i) => ({
      days: op.days,
      startTime: op.startTime,
      endTime: op.endTime,
      color: this.operationColor(i),
      sleep: op.sleep,
    }));
  }

  powerModeColor(mode: string): 'blue' | 'neutral' {
    return mode?.toUpperCase() === 'MAIN' ? 'blue' : 'neutral';
  }

  batteryStatusColor(status: string): 'green' | 'orange' | 'red' | 'neutral' {
    switch (status?.toUpperCase()) {
      case 'NORMAL':
        return 'green';
      case 'LOW_POWER':
        return 'orange';
      case 'NO_POWER':
        return 'red';
      default:
        return 'neutral';
    }
  }

  batteryStatusLabel(status: string): string {
    return status
      .split('_')
      .map((w) => w[0] + w.slice(1).toLowerCase())
      .join(' ');
  }

  // ── Default (profile) CRUD + dialog ───────────────────────────────────────

  openAddDialog(): void {
    this.editingDefault.set(null);
    this.dialogMode.set('add');
    this.isProfileDialogOpen.set(true);
  }

  editSelectedDefault(): void {
    const def = this.selectedDefault();
    if (!def) return;
    this.editingDefault.set(def);
    this.dialogMode.set('edit');
    this.isProfileDialogOpen.set(true);
  }

  async deleteSelectedDefault(): Promise<void> {
    const def = this.selectedDefault();
    if (!def) return;
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.t('deleteConfirm.title'),
      message: this.t('deleteConfirm.body', { name: def.operationProfileId }),
      confirmLabel: this.t('deleteConfirm.confirm'),
      confirmWord: def.operationProfileId,
    });
    if (confirmed) this.deleteDefault(def);
  }

  closeProfileDialog(): void {
    this.isProfileDialogOpen.set(false);
    this.editingDefault.set(null);
  }

  handleProfileSave(request: OperationProfileRequest): void {
    const def = this.editingDefault();
    this.isProfileSaving.set(true);
    if (this.dialogMode() === 'edit' && def) {
      // Full-replace PUT: keep the existing operations so editing profile-level
      // fields does not wipe the default's operations.
      const payload: CreateOperationProfileRequest = {
        ...(request as CreateOperationProfileRequest),
        operations: def.operations.map(toOperationRequest),
      };
      this.service
        .updateDefault(def.id, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => this.onSaved(response.data, 'toasts.updated'),
          error: () => this.onSaveError('toasts.updateFailed'),
        });
    } else {
      const payload: CreateOperationProfileRequest = {
        ...(request as CreateOperationProfileRequest),
        operations: [],
      };
      this.service
        .createDefault(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.isProfileSaving.set(false);
            this.toastService.show(this.t('toasts.created'), 'success');
            this.closeProfileDialog();
            this.defaults.update((list) => [response.data, ...list]);
            this.selectedDefault.set(response.data);
          },
          error: () => this.onSaveError('toasts.createFailed'),
        });
    }
  }

  private deleteDefault(def: OperationProfile): void {
    this.service
      .deleteDefault(def.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('toasts.deleted'), 'success');
          this.defaults.update((list) => list.filter((d) => d.id !== def.id));
          if (this.selectedDefault()?.id === def.id) {
            this.selectedDefault.set(this.defaults()[0] ?? null);
          }
        },
        error: () => this.toastService.show(this.t('toasts.deleteFailed'), 'error'),
      });
  }

  // ── Operation CRUD (immediate full re-PUT of the default) ─────────────────

  openAddOperationDialog(): void {
    this.editingOperation.set(null);
    this.dialogMode.set('add');
    this.isOperationDialogOpen.set(true);
  }

  editOperation(operation: Operation): void {
    this.editingOperation.set(operation);
    this.dialogMode.set('edit');
    this.isOperationDialogOpen.set(true);
  }

  closeOperationDialog(): void {
    this.isOperationDialogOpen.set(false);
    this.editingOperation.set(null);
  }

  handleOperationSave(request: OperationRequest): void {
    const def = this.selectedDefault();
    if (!def) return;
    const existing = def.operations.map(toOperationRequest);
    const editing = this.editingOperation();

    // operationId is the business key; defaults' operations round-trip on it.
    const nextOps =
      this.dialogMode() === 'edit' && editing
        ? existing.map((o) => (o.operationId === editing.operationId ? request : o))
        : [...existing, request];

    this.putOperations(def, nextOps, 'toasts.operationSaved', 'toasts.operationSaveFailed');
  }

  deleteOperation(operation: Operation): void {
    const def = this.selectedDefault();
    if (!def) return;
    const nextOps = def.operations
      .filter((o) => o.operationId !== operation.operationId)
      .map(toOperationRequest);
    this.putOperations(def, nextOps, 'toasts.operationDeleted', 'toasts.operationDeleteFailed');
  }

  private putOperations(
    def: OperationProfile,
    operations: OperationRequest[],
    successKey: string,
    errorKey: string,
  ): void {
    const payload: CreateOperationProfileRequest = {
      operationProfileId: def.operationProfileId,
      powerMode: def.powerMode,
      lowPowerPercentage: def.lowPowerPercentage,
      lowPowerPercentageOut: def.lowPowerPercentageOut,
      noPowerPercentage: def.noPowerPercentage,
      noPowerPercentageOut: def.noPowerPercentageOut,
      renderInterval: def.renderInterval,
      connectionInterval: def.connectionInterval,
      allowWakeup: def.allowWakeup,
      operations,
    };
    this.service
      .updateDefault(def.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.toastService.show(this.t(successKey), 'success');
          this.closeOperationDialog();
          this.applyUpdated(response.data);
        },
        error: () => this.toastService.show(this.t(errorKey), 'error'),
      });
  }

  private onSaved(updated: OperationProfile, successKey: string): void {
    this.isProfileSaving.set(false);
    this.toastService.show(this.t(successKey), 'success');
    this.closeProfileDialog();
    this.applyUpdated(updated);
  }

  private onSaveError(errorKey: string): void {
    this.isProfileSaving.set(false);
    this.toastService.show(this.t(errorKey), 'error');
  }

  /** Apply a server-returned default to the list + selection (optimistic). */
  private applyUpdated(updated: OperationProfile): void {
    this.defaults.update((list) => list.map((d) => (d.id === updated.id ? updated : d)));
    if (this.selectedDefault()?.id === updated.id) {
      this.selectedDefault.set(updated);
    }
  }
}

/** Strip the response-only fields so an Operation can be re-sent in a PUT. */
function toOperationRequest(op: Operation): OperationRequest {
  return {
    operationId: op.operationId,
    sleep: op.sleep,
    shutdown: op.shutdown,
    renderInterval: op.renderInterval,
    connectionInterval: op.connectionInterval,
    illumination: op.illumination,
    startDate: op.startDate,
    endDate: op.endDate,
    startTime: op.startTime,
    endTime: op.endTime,
    days: op.days,
    batteryPowerStatusList: op.batteryPowerStatusList,
  };
}
